import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
  useFonts,
} from '@expo-google-fonts/inter';
import '@expo/metro-runtime'; // this enables fast-refresh in development...what happens in production?
import '@gripp/i18n';
import {
  EnvConfig,
  FeatureName,
  Graphback,
  GrippContext,
  currentWorkspaceQueryKey,
  fetchStoredCurrentWorkspace,
  isFeatureEnabledForContext,
  useAuthentication,
  useCurrentAccount,
  useFeatures,
  useGrippContext,
  useStorageKeys,
  useWatchLocation,
} from '@gripp/shared-logic';
import type { Feature, Workspace } from '@gripp/shared-logic';
import {
  setUseCropCircleDesign,
  SystemNotification,
  SystemNotificationProvider,
} from '@gripp/shared-ui';
import Clarity from '@microsoft/clarity';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useMemo, useState } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { MediaPicker } from '../shared/components/media/mediaPicker';
import { SignatureScreenWeb } from '../shared/components/signature/signatureScreenWeb';
import { localStorageHandler } from '../shared/localStorageHandler';
import { ErrorBoundary } from '../shared/logging/errorBoundary';
import { SentryWebLogger } from '../shared/logging/sentryWebLogger';
import { routes } from './routing';

type AppRouter = ReturnType<typeof createBrowserRouter>;

export const App = () => {
  const [client] = useState(() => new QueryClient());
  const router = useMemo(
    () => SentryWebLogger.wrap(createBrowserRouter)(routes),
    []
  );
  const { watchLocation } = useWatchLocation(localStorageHandler);
  watchLocation(true);

  const clarityProjectId = EnvConfig.clarityProjectId;
  useEffect(() => {
    if (clarityProjectId) {
      Clarity.init(clarityProjectId);
    }
  }, [clarityProjectId]);

  const [fontsLoaded, fontsError] = useFonts({
    Inter: Inter_400Regular,
    InterMedium: Inter_500Medium,
    InterSemiBold: Inter_600SemiBold,
    InterBold: Inter_700Bold,
    TwemojiMozilla: '/TwemojiMozilla.woff2',
  });

  if (!fontsLoaded && !fontsError) {
    return null;
  }

  return (
    <GrippContext.Provider
      value={{
        logger: SentryWebLogger,
        secureStorageHandler: localStorageHandler,
        storageHandler: localStorageHandler,
        mediaPickerComponent: MediaPicker,
        signaturePadScreen: SignatureScreenWeb,
      }}
    >
      <QueryClientProvider client={client}>
        <ErrorBoundary>
          <SystemNotificationProvider>
            <AppRoutes router={router} />
            <SystemNotification />
          </SystemNotificationProvider>
        </ErrorBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </GrippContext.Provider>
  );
};

const AppRoutes = ({ router }: { router: AppRouter }) => {
  const authentication = useAuthentication();

  if (authentication?.isAuthenticated && authentication.accessToken) {
    Graphback.initialize(authentication.accessToken);
  }

  useEffect(() => {
    if (!authentication || authentication.isLoading) {
      return;
    }

    if (!authentication.isAuthenticated || !authentication.accessToken) {
      setUseCropCircleDesign(false);
      Graphback.dispose();
    }
  }, [authentication]);

  if (!authentication || authentication.isLoading) {
    return null;
  }

  if (!authentication.isAuthenticated || !authentication.accessToken) {
    return <RouterProvider router={router} />;
  }

  return <CropCircleBootstrap router={router} />;
};

const CropCircleBootstrap = ({ router }: { router: AppRouter }) => {
  const {
    account,
    isLoading: isAccountLoading,
    isFetching: isAccountFetching,
  } = useCurrentAccount();
  const { data: storedWorkspace, isLoading: isStoredWorkspaceLoading } =
    useStoredCurrentWorkspace();
  const { data: featuresData, isLoading: isFeaturesLoading } = useFeatures();
  const [hasAppliedCropCircle, setHasAppliedCropCircle] = useState(false);

  const features: Feature[] = featuresData?.items ?? [];
  const currentWorkspace = storedWorkspace ?? account?.workspace;
  const cropCircleFeature = features.find(
    (feature) => feature.name === FeatureName.CropCircleRedesign
  );
  const isCropCircleRedesignEnabled = isFeatureEnabledForContext(
    cropCircleFeature,
    currentWorkspace?.id,
    account?.id
  );
  const isAccountPending = isAccountLoading || (!account && isAccountFetching);
  const isCropCircleCheckPending =
    isAccountPending || isStoredWorkspaceLoading || isFeaturesLoading;

  useEffect(() => {
    if (isCropCircleCheckPending) {
      return;
    }

    setUseCropCircleDesign(isCropCircleRedesignEnabled);
    setHasAppliedCropCircle(true);
  }, [isCropCircleCheckPending, isCropCircleRedesignEnabled]);

  if (isCropCircleCheckPending || !hasAppliedCropCircle) {
    return null;
  }

  return <RouterProvider router={router} />;
};

const useStoredCurrentWorkspace = () => {
  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();

  return useQuery<Workspace | null>({
    staleTime: Infinity,
    queryKey: currentWorkspaceQueryKey,
    queryFn: () => fetchStoredCurrentWorkspace(storageHandler, storageKeys),
  });
};

export default App;
